import { useState } from 'react'
import { TalentRankSummary } from '../../../types/definitions/talentRank'

type EmployeeResultsScoreCardProps = {
  summary?: TalentRankSummary
  selectedTalentRank?: string
  overrideJustification?: string
}

const formatValue = (value?: string | number): string => {
  if (typeof value === 'number') {
    return `${value}`
  }
  return value ?? 'Not available'
}

export const EmployeeResultsScoreCard = ({
  summary,
  selectedTalentRank,
  overrideJustification,
}: EmployeeResultsScoreCardProps): JSX.Element | null => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!summary && !selectedTalentRank) {
    return null
  }

  const averageScore = typeof summary?.averageScore === 'number' ? `${summary.averageScore}%` : 'Not available'
  const suggestedComparable = summary?.suggestionValue ?? summary?.categoryLabel
  const suggestedTalentRankLabel = formatValue(suggestedComparable)
  const selectedTalentRankLabel = formatValue(selectedTalentRank)
  const displaySingleTalentRank = suggestedTalentRankLabel === selectedTalentRankLabel
  const showDifference = Boolean(
    !displaySingleTalentRank &&
      selectedTalentRank &&
      suggestedComparable &&
      selectedTalentRank !== suggestedComparable &&
      overrideJustification &&
      overrideJustification.trim().length
  )
  const gridColumnsClass = displaySingleTalentRank ? 'md:grid-cols-2' : 'md:grid-cols-3'

  return (
    <div className="bg-pale-cyan rounded-2xl mb-8 px-6 py-8 md:px-10 md:py-10">
      <div className="text-2xl font-bold text-dark-blue mb-6">Score & Talent Category</div>
      <div className={`grid gap-8 ${gridColumnsClass}`}>
        <div>
          <div className="text-sm uppercase tracking-wide text-orange font-semibold">Latest score</div>
          <div className="text-4xl font-bold text-dark-blue mt-2">{averageScore}</div>
        </div>
        {displaySingleTalentRank ? (
          <div>
            <div className="text-sm uppercase tracking-wide text-orange font-semibold">Talent Category</div>
            <div className="text-2xl font-semibold text-dark-blue mt-2">{suggestedTalentRankLabel}</div>
          </div>
        ) : (
          <>
            <div>
              <div className="text-sm uppercase tracking-wide text-orange font-semibold">Suggested talent category</div>
              <div className="text-2xl font-semibold text-dark-blue mt-2">{suggestedTalentRankLabel}</div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-orange font-semibold">Selected talent category</div>
              <div className="text-2xl font-semibold text-dark-blue mt-2">{selectedTalentRankLabel}</div>
              {showDifference && (
                <div className="text-sm text-toast-hover mt-2">
                  Suggested category differs from the selected category.
                  <button className="ml-2 text-orange underline" onClick={() => setIsModalOpen(true)}>
                    View justification
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {isModalOpen && overrideJustification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-6 sm:p-10">
          <div className="bg-white w-full max-w-3xl rounded-3xl border border-orange/10 shadow-[0_24px_80px_rgba(15,23,42,0.18)] overflow-hidden">
            <header className="flex items-center justify-between gap-4 bg-gradient-to-r from-pale-cyan to-white px-8 sm:px-12 py-6 sm:py-8">
              <span className="text-2xl font-semibold text-dark-blue">Override Justification</span>
              <button
                className="inline-flex items-center rounded-full border border-orange/20 px-4 py-2 text-sm font-medium text-orange hover:bg-pale-cyan hover:text-dark-blue transition-colors"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </button>
            </header>
            <section className="px-8 sm:px-12 py-8 sm:py-12 bg-white">
              <div className="max-h-[60vh] overflow-y-auto pr-1 text-dark-blue text-lg leading-relaxed whitespace-pre-wrap">
                {overrideJustification}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeResultsScoreCard
